/* eslint-disable no-param-reassign */

import axios, {
    AxiosError,
    AxiosRequestConfig,
    AxiosRequestHeaders,
} from "axios";
import * as Sentry from "@sentry/react";
import { auth } from "../firebase";
import { store } from "../redux/store";

const baseURL = import.meta.env.VITE_API_BASE_URL;
// TODO: Reduce this timeout after the current long-running API operations are optimized.
const REQUEST_TIMEOUT = 120000;

if (!baseURL) {
    throw new Error("VITE_API_BASE_URL is not defined");
}

const axiosInstance = axios.create({
    baseURL,
});

async function getAccessToken(forceRefresh = false) {
    if (!auth.currentUser) {
        return undefined;
    }

    const token = await auth.currentUser.getIdToken(forceRefresh);
    return token;
}

function logAxiosWarning(
    message: string,
    config?: AxiosRequestConfig,
    extra?: Record<string, unknown>
) {
    Sentry.logger.warn(message, {
        area: "axios",
        method: config?.method,
        url: config?.url,
        ...extra,
    });
}

function getFirebaseErrorContext(firebaseError?: unknown) {
    const context: Record<string, unknown> = {};

    if (firebaseError && firebaseError instanceof Error) {
        const typedFirebaseError = firebaseError as Error & { code?: string };
        context.firebaseError = typedFirebaseError.message;
        context.firebaseErrorCode = typedFirebaseError.code ?? "unknown";
    }

    return context;
}

function getPracticeId() {
    const state = store.getState();
    const practiceId = state.userPractice?.practice?.practice_id;

    if (!practiceId) {
        throw new Error("Practice details not found");
    }

    return practiceId;
}

function isPracticeAgnosticRequest(config: AxiosRequestConfig) {
    const method = config.method?.toLowerCase();
    const url = config.url ?? "";

    if (method === "get" && /^\/?user(?:\?.*)?$/.test(url)) {
        return true;
    }

    if (method === "get" && /^\/?generate-jitsi-token(?:\?.*)?$/.test(url)) {
        return true;
    }

    if (method === "post" && /^\/?user\/sync-google-name$/.test(url)) {
        return true;
    }

    return /^\/?auth\/mfa(?:\/|\?|$)/.test(url);
}

function sanitizeParams(params: unknown): unknown {
    if (
        !params ||
        typeof params !== "object" ||
        params instanceof URLSearchParams
    ) {
        return params;
    }

    return Object.fromEntries(
        Object.entries(params).map(([key, value]) => [
            key,
            typeof value === "string" ? value.replace(/,+/g, "") : value,
        ])
    );
}

function captureAxiosFailure(
    message: string,
    config?: AxiosRequestConfig,
    extra?: Record<string, unknown>
) {
    Sentry.withScope((scope) => {
        scope.setLevel("warning");
        scope.setTag("area", "axios");
        scope.setContext("request", {
            method: config?.method ?? "unknown",
            url: config?.url ?? "unknown",
            ...extra,
        });
        Sentry.captureException(new Error(message));
    });
}

interface RetryAxiosRequestConfig extends AxiosRequestConfig {
    retry?: boolean;
    skipAuthOverride?: boolean;
    skipUnauthorizedLogout?: boolean;
}

type AxiosTimeoutError = Error & {
    code: "ECONNABORTED";
    isTimeout: true;
};

type UnauthorizedLogoutHandler = () => Promise<void> | void;

let onUnauthorizedLogout: UnauthorizedLogoutHandler | null = null;
let isLoggingOut = false;

function createTimeoutError(): AxiosTimeoutError {
    const error = new Error(
        "Request took longer than usual. Please try again."
    ) as AxiosTimeoutError;

    error.code = "ECONNABORTED";
    error.isTimeout = true;

    return error;
}

export function setUnauthorizedLogoutHandler(
    handler: UnauthorizedLogoutHandler | null
) {
    onUnauthorizedLogout = handler;
}

async function runUnauthorizedLogout() {
    if (!onUnauthorizedLogout || isLoggingOut) {
        return;
    }

    isLoggingOut = true;

    try {
        await onUnauthorizedLogout();
    } catch {
        return;
    } finally {
        isLoggingOut = false;
    }
}

function buildRetryRequestConfig(
    originalRequest: RetryAxiosRequestConfig,
    token: string
): RetryAxiosRequestConfig {
    const headers = {
        ...(originalRequest.headers ?? {}),
        Authorization: `Bearer ${token}`,
    };

    return {
        ...originalRequest,
        headers,
        signal: undefined,
    };
}

function hasAuthorizationHeader(headers: Record<string, unknown>) {
    return Boolean(headers.Authorization ?? headers.authorization);
}

function isBearerAuthorizationHeader(value: unknown) {
    return typeof value === "string" && value.startsWith("Bearer ");
}

function shouldSkipAuthOverride(config: RetryAxiosRequestConfig) {
    return config.skipAuthOverride === true;
}

function shouldSkipUnauthorizedLogout(config: RetryAxiosRequestConfig) {
    return config.skipUnauthorizedLogout === true;
}

axiosInstance.interceptors.request.use(
    async (config) => {
        const requestConfig = config as RetryAxiosRequestConfig;
        const isPracticeAgnostic = isPracticeAgnosticRequest(config);

        if (config.method?.toLowerCase() === "get") {
            config.timeout = REQUEST_TIMEOUT;
        } else {
            config.timeout = 0;
        }

        if (!config.signal) {
            const controller = new AbortController();
            config.signal = controller.signal;
        }

        let token = await getAccessToken();
        const hasAuthenticatedUser = Boolean(auth.currentUser);

        let practiceId: string | undefined;
        if (hasAuthenticatedUser && !isPracticeAgnostic) {
            try {
                practiceId = getPracticeId();
            } catch (error) {
                logAxiosWarning(
                    "Missing practice_id in request context",
                    config,
                    {
                        hasAuthenticatedUser,
                        error:
                            error instanceof Error ? error.message : "unknown",
                    }
                );
                throw error;
            }
        }

        const headers = {
            ...(config.headers ?? {}),
        } as AxiosRequestHeaders;

        const shouldPreserveAuthorizationHeader =
            shouldSkipAuthOverride(requestConfig) &&
            hasAuthorizationHeader(headers);

        const hasCustomAuthorizationHeader =
            hasAuthorizationHeader(headers) &&
            !isBearerAuthorizationHeader(
                headers.Authorization ?? headers.authorization
            );

        if (
            hasAuthenticatedUser &&
            !hasCustomAuthorizationHeader &&
            !shouldPreserveAuthorizationHeader &&
            !token
        ) {
            token = await getAccessToken(true);

            if (!token) {
                logAxiosWarning("Missing access token before request", config, {
                    userId: auth.currentUser?.uid ?? "unknown",
                });
                throw new Error("Missing access token for authenticated user");
            }
        }

        if (
            token &&
            !hasCustomAuthorizationHeader &&
            !shouldPreserveAuthorizationHeader
        ) {
            headers.Authorization = `Bearer ${token}`;
        }

        if (practiceId) {
            headers.practice_id = practiceId;
        }

        config.headers = headers;

        if (config.params) {
            config.params = sanitizeParams(config.params);
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

let isRefreshing = false;
let failedQueue: {
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
    logoutOnRefreshFailure: boolean;
}[] = [];

function processQueue(error: unknown, token: string | null = null) {
    let shouldLogout = false;

    failedQueue.forEach((prom) => {
        if (error) {
            shouldLogout ||= prom.logoutOnRefreshFailure;
            prom.reject(error);
        } else if (token) {
            prom.resolve(token);
        }
    });
    failedQueue = [];

    return shouldLogout;
}

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }

        const axiosError = error as AxiosError;

        if (axiosError.code === "ECONNABORTED") {
            logAxiosWarning("Request timeout", axiosError.config, {
                code: axiosError.code,
            });
            return Promise.reject(createTimeoutError());
        }

        const originalRequest = axiosError.config as
            | RetryAxiosRequestConfig
            | undefined;

        if (axiosError.response?.status !== 401 || !originalRequest) {
            return Promise.reject(error);
        }

        if (originalRequest.retry) {
            captureAxiosFailure("Terminal 401 after retry", originalRequest, {
                status: axiosError.response?.status,
            });
            if (!shouldSkipUnauthorizedLogout(originalRequest)) {
                await runUnauthorizedLogout();
            }
            return Promise.reject(error);
        }

        originalRequest.retry = true;

        if (isRefreshing) {
            return new Promise<string>((resolve, reject) => {
                failedQueue.push({
                    resolve,
                    reject,
                    logoutOnRefreshFailure:
                        !shouldSkipUnauthorizedLogout(originalRequest),
                });
            })
                .then((token) => {
                    const retryRequest = buildRetryRequestConfig(
                        originalRequest,
                        token
                    );
                    return axiosInstance(retryRequest);
                })
                .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;

        let tokenRefreshError: unknown;

        try {
            let newToken: string | undefined;

            try {
                newToken = await getAccessToken(true);
            } catch (firebaseError) {
                tokenRefreshError = firebaseError;
                throw firebaseError;
            }

            if (!newToken) {
                logAxiosWarning(
                    "Token refresh returned empty token",
                    originalRequest,
                    {
                        status: axiosError.response?.status,
                        userId: auth.currentUser?.uid ?? "unknown",
                    }
                );
                throw axiosError;
            }

            processQueue(null, newToken);

            const retryRequest = buildRetryRequestConfig(
                originalRequest,
                newToken
            );
            return await axiosInstance(retryRequest);
        } catch (refreshError) {
            logAxiosWarning("Token refresh failed", originalRequest, {
                status: axiosError.response?.status,
                userId: auth.currentUser?.uid ?? "unknown",
                error:
                    refreshError instanceof Error
                        ? refreshError.message
                        : "unknown",
                ...getFirebaseErrorContext(tokenRefreshError),
            });
            const queuedRequestRequiresLogout = processQueue(
                refreshError,
                null
            );
            if (
                queuedRequestRequiresLogout ||
                !shouldSkipUnauthorizedLogout(originalRequest)
            ) {
                await runUnauthorizedLogout();
            }
            throw refreshError;
        } finally {
            isRefreshing = false;
        }
    }
);

export default axiosInstance;
